import { useEffect, useRef, useState } from 'react';

import type { RefObject } from 'react';

import { logger } from '@treneo/shared/logger';

import { getLocalStorageItem, setLocalStorageItem } from './local-storage-utils';

export const useOutsideClick = (ref: RefObject<HTMLElement>, handler: (event: Event) => void) => {
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [ref, handler]);
};

export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      window.clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const useLocalStorage = <T>(
  key: string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void] => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = getLocalStorageItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      logger.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      setLocalStorageItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      logger.warn(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
};

export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

type ImmediateIntervalCallback = () => void;

export const useImmediateInterval = (
  callback: ImmediateIntervalCallback,
  delay: number,
  clearingStatement = false
) => {
  const savedCallback = useRef<ImmediateIntervalCallback>(callback);

  useEffect(() => {
    callback();
  }, [callback]);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    const tick = () => {
      savedCallback.current?.();
    };

    if (delay !== null) {
      const id = window.setInterval(tick, delay);
      clearingStatement && window.clearInterval(id);
      return () => window.clearInterval(id);
    }

    return undefined;
  }, [delay, clearingStatement]);
};

type UseBoolean = [boolean, () => void, () => void];
export const useBoolean = (defaultValue = false): UseBoolean => {
  const [value, setValue] = useState(defaultValue);

  const setValueToTrue = () => {
    setValue(true);
  };

  const setValueToFalse = () => {
    setValue(false);
  };

  return [value, setValueToTrue, setValueToFalse];
};

export const useToggle = (initialValue = false): [boolean, () => void] => {
  const [value, setValue] = useState(initialValue);
  const toggle = () => setValue(currentValue => !currentValue);
  return [value, toggle];
};

export const useElementInFullscreen = (refElement: RefObject<HTMLDivElement>) => {
  const [isFullscreenEnabled, setFullscreenMode, setInitialMode] = useBoolean();

  const enterFullscreenMode = () => {
    refElement.current?.requestFullscreen();
  };

  const exitFullscreenMode = () => {
    document.exitFullscreen();
  };

  const toggleFullscreenMode = () => {
    isFullscreenEnabled ? exitFullscreenMode() : enterFullscreenMode();
  };

  const settersRef = useRef({ setFullscreenMode, setInitialMode });
  settersRef.current = { setFullscreenMode, setInitialMode };

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (document.fullscreenElement) {
        settersRef.current.setFullscreenMode();
      } else {
        settersRef.current.setInitialMode();
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return {
    isFullscreenEnabled,
    enterFullscreenMode,
    exitFullscreenMode,
    toggleFullscreenMode
  };
};
